import React, { useState, useEffect, useMemo } from "react";
import {
  ReloadOutlined, EditOutlined, EyeOutlined, SearchOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import debounce from "lodash/debounce";
import { useDispatch, useSelector } from "react-redux";
import {
  Card, Typography, Input, Table, Button, Space, message, Switch, Modal, Dropdown, Grid, Tag, AutoComplete,
  Tooltip
} from "antd";
import { useNavigate } from "react-router-dom";

import phoneIcon from "../../assets/icons/akar-icons_phone.svg";
import emailIcon from "../../assets/icons/carbon_email.svg";
import locationIcon from "../../assets/icons/akar-icons_location.svg";
import { listEmployersAccounts } from "../../features/ptmAccounts/ptmAccountSlice";
import ptmAccountApi from "../../features/ptmAccounts/api";
import ModalCommon from "../common/ModalCommon";
import "../../assets/css/create-ptm-form.css";
import { getStatusStyle } from "../../utils/Utils";
import dayjs from "dayjs";

const { Text } = Typography;
const { Search } = Input;
const { useBreakpoint } = Grid;

const EmployersAccountsList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const BASE_URL = process.env.REACT_APP_S3_URL || "https://skillgigs-v3-prod-documents-cl.s3.us-east-2.amazonaws.com";
  console.log("BASE_URL:", BASE_URL);
  const [searchText, setSearchText] = useState("");
  const [statusToggleLoading, setStatusToggleLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [provisioningCheckLoading, setProvisioningCheckLoading] = useState(false);
  const [deactivateModal, setDeactivateModal] = useState({ visible: false, record: null });
  const [approverName, setApproverName] = useState("");
  const [modalData, setModalData] = useState([]);
  const [isActive, setIsActive] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [domainOptions, setDomainOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const onChangeStatus = (checked) => {
    console.log(`switch to ${checked}`);

    setIsActive(!isActive);
  }

  const openModal = (e, id, employerName, status) => {
    e.stopPropagation();
    if (status === "deploying" || status === "failed") return;
    setIsActive(status?.toLowerCase() === "active")
    setIsModalOpen(true);
    const modalData = {
      status: status,
      employerName: employerName,
      id: id,
    }
    setModalData(modalData);
  };



  const { ptmEmployers, isLoading, pagination } = useSelector((state) => state.ptmAccounts);
  const apiData = ptmEmployers || [];

  const filteredData = useMemo(() => apiData, [apiData]);

  // const toggleStatus = async (checked, id) => {
  //   try {
  //     setStatusToggleLoading(id);
  //     await ptmAccountApi.changePTMStatus(checked ? "active" : "inactive", id);
  //     message.success("Status updated!");
  //     dispatch(listEmployersAccounts({ page, limit: pageSize }));
  //   } catch (error) {
  //     message.error(error?.message || "Error updating status.");
  //   } finally {
  //     setStatusToggleLoading(false);
  //   }
  // };

  const handleDeactivateConfirm = () => {
    if (!approverName.trim()) return message.error("Approver name is required!");
    message.success(`${deactivateModal.record?.employerName} has been deactivated by ${approverName}`);
    setDeactivateModal({ visible: false, record: null });
    setApproverName("");
  };

  const checkProvisioning = async () => {
    try {
      setProvisioningCheckLoading(true);
      const resp = await ptmAccountApi.checkPtmProvisioning();
      if (resp?.status === 200) navigate("/create-form");
      else throw resp;
    } catch (error) {
      message.error(error?.response?.data?.message || "Error checking PTM provisioning.");
    } finally {
      setProvisioningCheckLoading(false);
    }
  };
  const buildImageUrl = (path) => {
    if (!path || path.startsWith("http")) return path || "";
    return `${BASE_URL.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
  };

  const getAvatar = (name) => `https://ui-avatars.com/api/?name=${encodeURIComponent(name || "E")}&background=1890ff&color=fff&bold=true&size=128`;

  const MobileCard = ({ record }) => (
    <Card size="small" style={{ borderRadius: 12, marginBottom: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <Space size={12}>
          <img
            src={record.logoImage?.desktop || getAvatar(record.employerName)}
            alt={record.employerName}
            style={{ width: 48, height: 48, borderRadius: 10, objectFit: "contain", backgroundColor: "#f5f5f5", border: "1px solid #e8e8e8" }}
            onError={(e) => { e.target.onerror = null; e.target.src = getAvatar(record.employerName); }}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Text strong style={{ color: "#1890ff", fontSize: 15 }}>{record.employerName || "N/A"}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{record.profession || "N/A"}</Text>
          </div>
        </Space>

        <Space size={6}>
          <Tooltip title={record.status == 'deploying' ? "You can not edit or deactivate during deployment" : ""}>
            <Button disabled={record.status == 'deploying'} type="text" icon={<EditOutlined />} onClick={() => navigate(`/create-form/${record.AccountId}`)} />
          </Tooltip>
          <Button type="text" icon={<EyeOutlined />} onClick={() => navigate(`/create-form/${record.AccountId}?mode=view`)} />
          <Tooltip title={record.status == 'deploying' ? "You can not edit or deactivate during deployment" : ""}>
            <Button disabled={record.status == 'deploying'} type="text" danger icon={<DeleteOutlined />} onClick={() => { setDeactivateModal({ visible: true, record }); setApproverName(""); }} />
          </Tooltip>
        </Space>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "12px 0", borderTop: "1px solid #f0f0f0", borderBottom: "1px solid #f0f0f0" }}>
        <Space size={8}><img src={emailIcon} style={{ width: 14 }} alt="email" /><Text type="secondary" style={{ fontSize: 13, wordBreak: "break-all" }}>{record.email || "No email"}</Text></Space>
        <Space size={8}><img src={phoneIcon} style={{ width: 14 }} alt="phone" /><Text type="secondary" style={{ fontSize: 13 }}>{record.phoneNumber || "N/A"}</Text></Space>
        <Space size={8}><img src={locationIcon} style={{ width: 14 }} alt="location" /><Text type="secondary" style={{ fontSize: 13 }}>{record.employerLocation || "N/A"}</Text></Space>

        <div><Text strong>Sub Domain: </Text><Text>{record.subDomain || "N/A"}</Text></div>
        <div>
          <Text strong>Landing Page URL: </Text>
          {record.employerDomain ? (
            <a href={`https://${record.employerDomain}`} target="_blank" rel="noopener noreferrer" style={{ color: "#1890ff", textDecoration: "underline", wordBreak: "break-word" }}>
              {record.employerDomain}
            </a>
          ) : <Text type="secondary">N/A</Text>}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
          <Text type="secondary">Created: {record.createdAt ? dayjs(record.createdAt).format("MM-DD-YYYY") : "N/A"}</Text>
          <Text type="secondary">Updated: {record.updatedAt ? dayjs(record.updatedAt).format("MM-DD-YYYY") : "N/A"}</Text>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
        <Text type="secondary">Status:</Text>
        {record.status === "deploying" ? <Tag color="processing">Deploying</Tag> : (
          <Text onClick={(e) => openModal(e, record.AccountId, record.employerName, record.status)} style={getStatusStyle(record.status)} type="secondary">{record.status}</Text>
        )}
      </div>
    </Card>
  );

  const columns = [
    {
      title: "Client Name", key: "details",
      render: (_, record) => {
        if (!record) return null;
        return (
          <Space size={12}>
            <img src={buildImageUrl(record.logoImage?.desktop || record.logoImage?.mobile) || getAvatar(record.employerName)} alt={record.employerName}
              style={{ width: 42, height: 42, borderRadius: 8, objectFit: "contain", backgroundColor: "#f5f5f5", border: "1px solid #e8e8e8" }}
              onError={(e) => { e.target.onerror = null; e.target.src = getAvatar(record.employerName); }} />
            <div>
              <div style={{ fontWeight: "bold", color: "#1890ff", fontSize: 15, textTransform: "capitalize" }}>{record.employerName || "N/A"}</div>
              <Space size={4} style={{ marginTop: 4 }}><img src={emailIcon} style={{ width: 14 }} alt="email" /><Text type="secondary">{record.email || "No email"}</Text></Space>
              <br />
              <Space size={20} style={{ marginTop: 4 }}>
                <Space size={4}><img src={phoneIcon} style={{ width: 14 }} alt="phone" /><Text type="secondary" style={{ whiteSpace: "nowrap", wordBreak: "keep-all" }}>{record.phoneNumber || "N/A"}</Text></Space>
                <span>•</span>
                <Space size={4}><img src={locationIcon} style={{ width: 14 }} alt="location" /><Text type="secondary" style={{ whiteSpace: "nowrap", wordBreak: "keep-all" }}>{record.employerLocation || "N/A"}</Text></Space>
              </Space>
            </div>
          </Space>
        );
      },
    },
    { title: "Profession", dataIndex: "profession", align: "center", render: (text) => <Text strong>{text || "N/A"}</Text> },
    {
      title: "Sub Domain",
      dataIndex: "subDomain",
      align: "center",
      render: (text) => <Text>{text || "N/A"}</Text>,
    },
    {
      title: "Landing Page URL",
      dataIndex: "employerDomain",
      align: "center",
      render: (text) =>
        text ? <a href={`https://${text}`} target="_blank" style={{ color: "#1890ff", textDecoration: "underline" }}>{text}</a> : <Text type="secondary">N/A</Text>,
    },
    {
      title: "Status", align: "center",
      render: (_, record) => (
        <Space direction="vertical">
          {record.status === "deploying" ? (
            <Button type="link" icon={<EyeOutlined />} style={{ color: "#52c41a" }} onClick={() => record.executionId && navigate(`/provision/${record.executionId}`)}>View</Button>
          ) : (
            <Text onClick={(e) => openModal(e, record.AccountId, record.employerName, record.status)} style={getStatusStyle(record.status)} type="secondary">{record.status}</Text>
          )}
        </Space>
      ),
    },
    { title: "Created", dataIndex: "createdAt", align: "center", render: (text) => <Text strong>{text ? dayjs(text).format("MM-DD-YYYY") : "N/A"}</Text> },
    { title: "Updated", dataIndex: "updatedAt", align: "center", render: (text) => <Text strong>{text ? dayjs(text).format("MM-DD-YYYY") : "N/A"}</Text> },
    {
      title: "Action", align: "center",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title={record.status == 'deploying' ? "You can not edit or deactivate during deployement" : ""}>
            <Button disabled={record.status == 'deploying'} type="text" icon={<EditOutlined />} onClick={() => navigate(`/create-form/${record.AccountId}`)} />
          </Tooltip>
          <Button type="link" icon={<EyeOutlined />} style={{ color: "#52c41a" }} onClick={() => navigate(`/create-form/${record.AccountId}?mode=view`)}>View</Button>
          <Tooltip title={record.status == 'deploying' ? "You can not edit or deactivate during deployement" : ""}>
            <Button disabled={record?.status == 'deploying'} type="link" icon={<DeleteOutlined />} size="small" onClick={() => { setDeactivateModal({ visible: true, record }); setApproverName(""); }}>Deactivate</Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  useEffect(() => { dispatch(listEmployersAccounts({ page: 1, limit: 10 })); }, [dispatch]);
  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setPage(1);
        dispatch(
          listEmployersAccounts({
            page: 1,
            limit: pageSize,
            subDomain: value,
          })
        );
      }, 1000),
    [dispatch, pageSize]
  );

  const fetchDomains = async (value) => {
    if (!value) {
      setDomainOptions([]);
      return;
    }

    try {
      setSearchLoading(true);
      const res = await ptmAccountApi.searchDomains(value);

      const options = res.data.data.map((item) => ({
        value: item.subDomain,
        label: item.subDomain,
      }));
      setDomainOptions(options);
    } catch (err) {
      console.error(err);
    } finally {
      setSearchLoading(false);
    }
  };

  const debouncedDomainSearch = useMemo(
    () => debounce(fetchDomains, 500),
    []
  );

  return (
    <div style={{ padding: isMobile ? 12 : 24, background: "#f8f9fa", minHeight: "100vh" }}>
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "stretch" : "center", gap: 12, padding: isMobile ? "12px 16px" : "16px 24px", backgroundColor: "#fff", borderBottom: "1px solid #f0f0f0", borderRadius: "8px 8px 0 0", marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? 18 : 20, fontWeight: 600, color: "#262626" }}>Manage PTM Clients</h2>
        <Button type="primary" size={isMobile ? "middle" : "large"} block={isMobile} style={{ backgroundColor: "#141445", borderColor: "#141445", borderRadius: 8, fontWeight: 600, boxShadow: "0 2px 4px rgba(20, 20, 69, 0.2)" }} onClick={checkProvisioning} disabled={provisioningCheckLoading} loading={provisioningCheckLoading}>+ Create PTM</Button>
      </div>

      <Card style={{ marginBottom: 16, borderRadius: 8 }} bodyStyle={{ padding: isMobile ? 12 : 24 }}>
        <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "stretch" : "center", gap: 12 }}>
          <AutoComplete
            options={domainOptions}
            onSearch={(value) => {
              setSearchText(value);
              debouncedDomainSearch(value);
              debouncedSearch(value);
            }}
            onSelect={(value) => {
              setSearchText(value);
              dispatch(
                listEmployersAccounts({
                  page: 1,
                  limit: pageSize,
                  subDomain: value,
                })
              );
            }}
            style={{ width: isMobile ? "100%" : "20rem" }}
          >
            <Input
              size={isMobile ? "middle" : "large"}
              placeholder="Search by sub domain..."
              suffix={searchLoading ? "Loading..." : <SearchOutlined />}
              allowClear
            />
          </AutoComplete>
          <Button type="primary" icon={<ReloadOutlined />} size={isMobile ? "middle" : "large"} block={isMobile} onClick={() => dispatch(listEmployersAccounts({ page: 1, limit: 10 }))}>Refresh</Button>
        </div>
      </Card>

      <Card bodyStyle={{ padding: isMobile ? 12 : 0 }} style={{ borderRadius: 8 }}>
        {isMobile ? (
          isLoading ? <div style={{ textAlign: "center", padding: 40 }}><Text type="secondary">Loading...</Text></div> :
            filteredData.length === 0 ? <div style={{ textAlign: "center", padding: 40 }}><Text type="secondary">No data found</Text></div> : (
              <>
                <div id="scrollable-container" style={{ overflowY: "auto", maxHeight: "calc(100vh - 150px)" }}>
                  {filteredData.map((record) => <MobileCard key={record._id} record={record} />)}
                </div>

                <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 16 }}>
                  <Button disabled={page === 1} onClick={() => { setPage(page - 1); dispatch(listEmployersAccounts({ page: page - 1, limit: pageSize })); }}>Previous</Button>
                  <Button disabled style={{ minWidth: 50 }}>{page}</Button>
                  <Button disabled={page * pageSize >= (pagination?.total || 0)} onClick={() => { setPage(page + 1); dispatch(listEmployersAccounts({ page: page + 1, limit: pageSize })); }}>Next</Button>
                </div>
              </>
            )
        ) : (
          <Table loading={isLoading} columns={columns} dataSource={filteredData} rowKey="_id"
            pagination={{ current: pagination?.page || 1, pageSize: pagination?.limit || 10, total: pagination?.total || apiData.length, onChange: (pg, ps) => { setPageSize(ps); setPage(pg); dispatch(listEmployersAccounts({ page: pg, limit: ps, subDomain: searchText })); } }} />
        )}
      </Card>

      {/* <ModalCommon title="Update Status" isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen}>
        {modalData && <div className="Modal-Container">
          <div className="sub-container">
            <div style={{ textTransform: "capitalize" }}> {modalData.employerName} </div>
            <div> <Switch checked={isActive} onChange={onChangeStatus} /> </div>
          </div>
        </div>}
      </ModalCommon> */}

      <Modal
        title="Warning"
        open={deactivateModal.visible}
        onCancel={() => {
          setDeactivateModal({ visible: false });
          setApproverName("");
        }}
        footer={null}
        width={500}
        centered
      >
        <strong>
          Deactivating this PTM will remove it from the server. Approval
          required.
        </strong>

        <Input
          placeholder="Enter Approver Name (required)"
          value={approverName}
          onChange={(e) => setApproverName(e.target.value)}
          size="large"
          style={{ margin: "24px 0" }}
        />

        <div style={{ textAlign: "right" }}>
          <Button
            onClick={() => {
              setDeactivateModal({ visible: false });
              setApproverName("");
            }}
            style={{ marginRight: 12 }}
          >
            Cancel
          </Button>

          <Button type="primary" danger onClick={handleDeactivateConfirm}>
            Confirm Deactivation
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default EmployersAccountsList;
